import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
    Container, Box, Typography, Button, Paper, Alert, Snackbar,
    TextField, Stack, CircularProgress, Chip, Divider
} from '@mui/material';
import {
    Public as PublicIcon,
    Search as SearchIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

interface CountryRiskData {
    country: string;
    summary: string;
    details: string;
    lastUpdated: string;
    status: 'success' | 'error' | 'not-found';
}

const FCDOCountryRiskPage: React.FC = () => {
    const [countryName, setCountryName] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [riskData, setRiskData] = useState<CountryRiskData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const fetchCountryRisk = async () => {
        if (!countryName.trim()) {
            setError('Please enter a country name');
            return;
        }

        setIsLoading(true);
        setError(null);
        setRiskData(null);

        try {
            const response = await fetch('/api/FCDOCountryRiskFunction', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ country: countryName.trim() }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const errorMessage = errorData?.error || `Request failed: ${response.status}`;
                throw new Error(errorMessage);
            }

            const responseData = await response.json();

            if (responseData.success) {
                setRiskData({
                    country: responseData.data.country,
                    summary: responseData.data.summary,
                    details: responseData.data.details,
                    lastUpdated: responseData.data.lastUpdated,
                    status: 'success'
                });
                setSnackbarMessage(`Risk information retrieved for ${responseData.data.country}`);
                setSnackbarOpen(true);
            } else {
                throw new Error(responseData.error || 'Failed to retrieve country risk information');
            }
        } catch (error) {
            console.error('Country risk fetch error:', error);
            setError(error instanceof Error ? error.message : 'Failed to retrieve country risk information');
            setRiskData({
                country: countryName,
                summary: '',
                details: '',
                lastUpdated: '',
                status: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            fetchCountryRisk();
        }
    };

    const stripHtmlTags = (html: string): string => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    };

    const resetForm = () => {
        setCountryName('');
        setRiskData(null);
        setError(null);
    };

    const getSeverityChip = (summary: string) => {
        const lowerSummary = stripHtmlTags(summary).toLowerCase();
        if (lowerSummary.includes('avoid all travel') || lowerSummary.includes('do not travel')) {
            return <Chip label="High Risk" color="error" size="small" />;
        } else if (lowerSummary.includes('avoid all but essential travel')) {
            return <Chip label="Elevated Risk" color="warning" size="small" />;
        } else if (lowerSummary.includes('see our travel advice')) {
            return <Chip label="Standard Risk" color="info" size="small" />;
        } else {
            return <Chip label="Consult Advice" color="default" size="small" />;
        }
    };

    const getRiskLevelColor = (summary: string, details: string) => {
        const combinedText = (stripHtmlTags(summary) + ' ' + stripHtmlTags(details)).toLowerCase();

        // High risk indicators
        if (combinedText.includes('avoid all travel') ||
            combinedText.includes('do not travel') ||
            combinedText.includes('extremely dangerous') ||
            combinedText.includes('high risk of') ||
            combinedText.includes('serious security threat')) {
            return 'error.main'; // Red
        }

        // Elevated risk indicators  
        if (combinedText.includes('avoid all but essential travel') ||
            combinedText.includes('increased risk') ||
            combinedText.includes('heightened security') ||
            combinedText.includes('caution advised') ||
            combinedText.includes('elevated risk')) {
            return 'warning.main'; // Amber/Orange
        }

        // Standard/Low risk
        if (combinedText.includes('see our travel advice') ||
            combinedText.includes('normal precautions') ||
            combinedText.includes('low risk') ||
            combinedText.includes('generally safe')) {
            return 'success.main'; // Green
        }

        // Default to info color for unclear cases
        return 'info.main'; // Blue
    };

    return (
        <Container maxWidth="xl" sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Header Section - matching other pages */}
            <Box sx={{ flexShrink: 0 }}>
                <Typography variant="h4" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 'bold',
                    mb: 2
                }}>
                    FCDO Country Risk
                </Typography>

                <Typography variant="body1" sx={{
                    mb: 3,
                    color: 'text.secondary'
                }}>
                    Get current travel risk information for any country from the UK Foreign, Commonwealth & Development Office (FCDO).
                </Typography>

                {/* Search Input Area */}
                <Paper
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: '8px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PublicIcon sx={{ color: 'primary.main' }} />
                        <TextField
                            label="Country Name"
                            variant="outlined"
                            value={countryName}
                            onChange={(e) => setCountryName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="e.g., France, Thailand, Japan"
                            disabled={isLoading}
                            sx={{ flexGrow: 1 }}
                            helperText="Enter the name of a country to get current travel risk information"
                        />
                        <Button
                            variant="contained"
                            onClick={fetchCountryRisk}
                            disabled={!countryName.trim() || isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                            sx={{ minWidth: 180, height: '56px' }}
                        >
                            {isLoading ? 'Searching...' : 'Get Risk Info'}
                        </Button>
                    </Box>
                </Paper>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
            </Box>

            {/* Results Area */}
            <Box sx={{
                flexGrow: 1,
                overflow: 'auto',
                mt: 2
            }}>
                {/* Country Risk Information */}
                {riskData && riskData.status === 'success' && (
                    <Paper sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: '8px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)'
                    }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                            <CheckCircleIcon sx={{ color: 'success.main' }} />
                            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                {riskData.country}
                            </Typography>
                            {riskData.summary && getSeverityChip(riskData.summary)}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {riskData.summary && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{
                                    color: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <InfoIcon fontSize="small" />
                                    Summary
                                </Typography>
                                <Box
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(riskData.summary) }}
                                    sx={{
                                        backgroundColor: 'action.hover',
                                        p: 2,
                                        borderRadius: 1,
                                        borderLeft: '4px solid',
                                        borderLeftColor: 'primary.main',
                                        '& p': { my: 1, lineHeight: 1.7 },
                                        '& a': { color: 'primary.main', textDecoration: 'underline' },
                                        '& abbr': { textDecoration: 'none' },
                                        '& h2': { fontSize: '1.1rem', fontWeight: 'bold', mt: 2, mb: 1, color: 'primary.main' },
                                        '& ul': { pl: 3, my: 1 },
                                        '& li': { my: 0.5 },
                                        '& .application-notice': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.15)',
                                            p: 2,
                                            borderRadius: 1,
                                            borderLeft: '4px solid',
                                            borderLeftColor: 'error.main',
                                            my: 2,
                                            '& p': { color: '#ff8a80' },
                                        },
                                        '& .call-to-action': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.10)',
                                            p: 2,
                                            borderRadius: 1,
                                            borderLeft: '4px solid',
                                            borderLeftColor: 'error.light',
                                            my: 2,
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        {riskData.details && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{
                                    color: getRiskLevelColor(riskData.summary, riskData.details),
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontWeight: 'bold'
                                }}>
                                    <WarningIcon fontSize="small" />
                                    Detailed Information
                                </Typography>
                                <Box
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(riskData.details) }}
                                    sx={{
                                        lineHeight: 1.7,
                                        fontSize: '0.875rem',
                                        '& h2': { fontSize: '1.25rem', fontWeight: 'bold', mt: 3, mb: 1, color: 'primary.main' },
                                        '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mt: 2, mb: 1, color: 'text.primary' },
                                        '& p': { my: 1 },
                                        '& ul': { pl: 3, my: 1 },
                                        '& li': { my: 0.5 },
                                        '& a': { color: 'primary.main', textDecoration: 'underline' },
                                        '& abbr': { textDecoration: 'none' },
                                        '& .application-notice': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.15)',
                                            p: 2,
                                            borderRadius: 1,
                                            borderLeft: '4px solid',
                                            borderLeftColor: 'error.main',
                                            my: 2,
                                            '& p': { color: '#ff8a80', fontWeight: 500 },
                                        },
                                        '& .call-to-action': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.10)',
                                            p: 2,
                                            borderRadius: 1,
                                            borderLeft: '4px solid',
                                            borderLeftColor: 'error.light',
                                            my: 2,
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        {riskData.lastUpdated && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {riskData.lastUpdated}
                            </Typography>
                        )}

                        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderTopColor: 'divider' }}>
                            <Button
                                variant="outlined"
                                onClick={resetForm}
                                startIcon={<PublicIcon />}
                                sx={{ minWidth: 150 }}
                            >
                                Search Another Country
                            </Button>
                        </Box>
                    </Paper>
                )}

                {/* No results state */}
                {!isLoading && !riskData && !error && (
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <PublicIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Ready to Check Country Risk
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Enter a country name above to retrieve current travel risk information from the FCDO
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default FCDOCountryRiskPage;
